import React, { useEffect, useState } from "react";
import { Modal, Row, Col, message } from "antd";
import { useFormik } from "formik";
import {
  CustomButton,
  FormSelect,
  CustomTextArea,
  useCustomToast,
  UploadWithTable,
  CustomTable
} from "../../../../../components";
import { EvaluationItem } from "../../components/ByRoles/ModalManageItem";
import { TechnicalConceptAPI } from "../../../api/technicalConceptAPI";
import { UploadedFile } from "../../../../../utils";

interface IModalAssessmentProps {
  isOpen: boolean;
  onClose: () => void;
  evaluation: EvaluationItem;
  refetchId?: () => void;
  onSuccess: () => void;
  status_id?: string;
}

const ModalAssessment: React.FC<IModalAssessmentProps> = ({
  isOpen,
  onClose,
  evaluation,
  refetchId,
  onSuccess,
  status_id,
}) => {
  const [status, setStatus] = useState<{ id: string; name: string }[]>([]);
  const [itemSelected, setItemSeleted] = useState<EvaluationItem[]>([]);
  const [files, setFiles] = React.useState<UploadedFile[]>(evaluation?.documents || []);
  const { showToast } = useCustomToast();

  const isValidEvaluation = () => {
    const { status_id, observations } = formik.values;
    return (
      status_id.trim() !== "" &&
      observations.trim() !== "" &&
      files && files.length > 0
    );
  };

  const formik = useFormik({
    initialValues: {
      status_id: evaluation?.status_id || "",
      observations: evaluation?.observations || "",
      files: files
    },
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("status_id", values.status_id);
        formData.append("observations", values.observations);

        values.files.forEach((file: any, i) => {
          formData.append(`documents[${i}]`, file.file instanceof File ? file.file : file);
        });

        await TechnicalConceptAPI.evaluateConceptPetitionItems(evaluation.id.toString(), formData);
        refetchId && refetchId();

        onClose();
        onSuccess();
      } catch (error) {
        console.error("Error al enviar la evaluación:", error);
        message.error("Ocurrió un error al evaluar el ítem");
      }
    },
  });

  React.useEffect(() => {
    formik.setFieldValue("files", files);
  }, [files]);

  const onChangeStatus = (value: string) => {
    formik.setFieldValue("status_id", value);
    const selectedStatus = status.find((item) => item.id == value);
    if (selectedStatus) {
      setItemSeleted([
        {
          ...evaluation,
          status: selectedStatus.name,
          status_id: selectedStatus.id,
          documents: [],
          observations: formik.values.observations || "",
        },
      ]);
    }
  }

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await TechnicalConceptAPI.fetchParameters(['concept_feature_status']);
        setStatus(data.concept_feature_status.map((item: any) => ({
          id: String(item.id),
          name: item.status,
        })) || []);
      } catch (error) {
        console.error("Error fetching status:", error);
      }
    };
    fetchStatus();
  }, []);

  useEffect(() => {
    setItemSeleted([evaluation]);
  }, [evaluation]);

  return (
    <Modal
      width='95vw'      
      title="Evaluación la característica"
      open={isOpen}
      onCancel={onClose}
      footer={status_id !== '37' ? [
        <CustomButton key="cancel" label="Cancelar" onClick={onClose} buttonType="secondary" />,
        <CustomButton key="save" label="Guardar" onClick={() => {
          if (!isValidEvaluation()) {
            showToast("error", "Por favor complete todos los campos y adjunte al menos un archivo.")
            return;
          }

          formik.handleSubmit();
        }} />,
      ] :
      [
        <CustomButton key="cancel" label="Finalizar" onClick={onClose} buttonType="secondary" />,
      ]}
    >
      <form onSubmit={formik.handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <FormSelect
              label="Estado de la característica"
              name="status_id_caracteristica"
              placeholder="Seleccione"
              value={formik.values.status_id}
              onChange={onChangeStatus}
              options={status}
              onBlur={() => formik.setFieldTouched("status_id", true)}
              disabled={status_id === '37'}
            />
          </Col>
        </Row>

        <CustomTable
          dataSource={itemSelected}
          columns={[
            { title: "Item", dataIndex: "id", key: "id" },
            { title: "Lista predefinida", dataIndex: "listaPredefinida", key: "listaPredefinida" },
            { title: "Característica o aspecto", dataIndex: "caracteristica", key: "caracteristica" },
            { title: "Descripción de la revisión", dataIndex: "descripcionRevision", key: "descripcionRevision" },
            { title: "Estado", dataIndex: "status", key: "status" },
          ]}
          pagination={false}
          style={{ marginTop: "20px" }}
        />

        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col span={24}>
            <CustomTextArea
              label="Observaciones"
              name="observations"
              placeholder="Ingrese observaciones"
              value={formik.values.observations}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={status_id === '37'}
            />
          </Col>
        </Row>

        <h3>Evidencia de la evaluación</h3>

        {/* 📌 Subida de archivos con tabla */}
        <Row style={{ marginTop: "20px" }}>
          <Col xs={24}>
            <UploadWithTable onFilesChange={setFiles} files={files} disabled={status_id === '37'} />
          </Col>
        </Row>
      </form>
    </Modal>
  );
};

export default ModalAssessment;